import {
  PhotoCamera,
  Save,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import PasswordStrengthBar from "../components/settings/PasswordStrengthBar";
import { userProfilesAPI } from "../services/api";
import { setUser } from "../store/slices/authSlice";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  hasProfilePicture,
} from "../utils/avatarUtils";
import {
  canSubmitPasswordChange,
  getPasswordChecks,
  isPasswordStrong,
} from "../utils/passwordStrength";

const emptyProfileForm = {
  first_name: "",
  last_name: "",
  arabic_name: "",
  english_name: "",
  job_title: "",
};

const emptyPasswordForm = {
  current_password: "",
  new_password: "",
  confirm_password: "",
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: { xs: "16px", sm: "16px" },
    minHeight: { xs: "56px", sm: "56px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: { xs: "16px", sm: "16px" },
  },
};

const formGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gap: 2,
  width: "100%",
};

const actionRowSx = {
  display: "flex",
  gap: 2,
  justifyContent: { xs: "center", sm: "flex-end" },
  flexDirection: { xs: "column", sm: "row" },
  alignItems: "center",
  width: "100%",
};

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [pictureFile, setPictureFile] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSuccess, setProfileSuccess] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  const passwordChecks = useMemo(
    () => getPasswordChecks(passwordForm.new_password),
    [passwordForm.new_password]
  );

  const passwordSaveEnabled = useMemo(
    () =>
      canSubmitPasswordChange({
        currentPassword: passwordForm.current_password,
        newPassword: passwordForm.new_password,
        confirmPassword: passwordForm.confirm_password,
      }),
    [passwordForm]
  );

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    setProfileError(null);
    try {
      const data = await userProfilesAPI.getMyProfile();
      setProfile(data);
      setProfileForm({
        first_name: data.user?.first_name || "",
        last_name: data.user?.last_name || "",
        arabic_name: data.arabic_name || "",
        english_name: data.english_name || "",
        job_title: data.job_title || "",
      });
    } catch (err) {
      setProfileError(err.message || t("accountSettings.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    return () => {
      if (picturePreview) {
        URL.revokeObjectURL(picturePreview);
      }
    };
  }, [picturePreview]);

  const handleProfileChange = (field) => (event) => {
    const value = event.target.value;
    setProfileForm((prev) => ({ ...prev, [field]: value }));
    if (profileErrors[field]) {
      setProfileErrors((prev) => ({ ...prev, [field]: "" }));
    }
    setProfileSuccess(null);
  };

  const handlePasswordChange = (field) => (event) => {
    const value = event.target.value;
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors((prev) => ({ ...prev, [field]: "" }));
    }
    setPasswordSuccess(null);
  };

  const handlePictureSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) {
      setProfileErrors((prev) => ({
        ...prev,
        profile_picture: t("accountSettings.invalidImageType"),
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setProfileErrors((prev) => ({
        ...prev,
        profile_picture: t("accountSettings.imageTooLarge"),
      }));
      return;
    }

    setProfileErrors((prev) => ({ ...prev, profile_picture: "" }));
    setPictureFile(file);
    if (picturePreview) {
      URL.revokeObjectURL(picturePreview);
    }
    setPicturePreview(URL.createObjectURL(file));
    setProfileSuccess(null);
  };

  const validateProfileForm = () => {
    const errors = {};
    if (!profileForm.arabic_name.trim()) {
      errors.arabic_name = t("referencePages.fieldRequired");
    }
    if (!profileForm.english_name.trim()) {
      errors.english_name = t("referencePages.fieldRequired");
    }
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordForm.current_password) {
      errors.current_password = t("accountSettings.currentPasswordRequired");
    }
    if (!passwordForm.new_password) {
      errors.new_password = t("accountSettings.newPasswordRequired");
    } else if (!passwordChecks.notCommon) {
      errors.new_password = t("accountSettings.passwordCommon");
    } else if (!isPasswordStrong(passwordForm.new_password)) {
      errors.new_password = t("accountSettings.passwordNotStrong");
    }
    if (!passwordForm.confirm_password) {
      errors.confirm_password = t("accountSettings.confirmPasswordRequired");
    } else if (passwordForm.new_password !== passwordForm.confirm_password) {
      errors.confirm_password = t("accountSettings.passwordMismatch");
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildAuthUserFromProfile = (updatedProfile) => {
    const picUrl = updatedProfile.profile_picture
      ? updatedProfile.profile_picture
      : updatedProfile.user?.profile_picture || null;
    return {
      id: updatedProfile.user?.id,
      username: updatedProfile.user?.username,
      email: updatedProfile.user?.email,
      first_name: updatedProfile.user?.first_name,
      last_name: updatedProfile.user?.last_name,
      is_staff: updatedProfile.user?.is_staff,
      is_active: updatedProfile.user?.is_active,
      profile_picture: picUrl,
      name:
        updatedProfile.arabic_name ||
        updatedProfile.english_name ||
        updatedProfile.user?.username,
      profile: {
        arabic_name: updatedProfile.arabic_name,
        english_name: updatedProfile.english_name,
        job_title: updatedProfile.job_title,
        mobile: updatedProfile.mobile,
        profile_picture: picUrl,
      },
    };
  };

  const handleSaveProfile = async () => {
    if (!validateProfileForm()) return;

    setIsSavingProfile(true);
    setProfileError(null);
    setProfileSuccess(null);

    try {
      const payload = new FormData();
      payload.append("first_name", profileForm.first_name.trim());
      payload.append("last_name", profileForm.last_name.trim());
      payload.append("arabic_name", profileForm.arabic_name.trim());
      payload.append("english_name", profileForm.english_name.trim());

      if (pictureFile) {
        payload.append("profile_picture", pictureFile);
      }

      const updated = await userProfilesAPI.updateMySettings(payload);
      setProfile(updated);
      setProfileForm({
        first_name: updated.user?.first_name || "",
        last_name: updated.user?.last_name || "",
        arabic_name: updated.arabic_name || "",
        english_name: updated.english_name || "",
        job_title: updated.job_title || "",
      });
      setPictureFile(null);
      if (picturePreview) {
        URL.revokeObjectURL(picturePreview);
        setPicturePreview(null);
      }
      dispatch(setUser(buildAuthUserFromProfile(updated)));
      setProfileSuccess(t("accountSettings.saveProfileSuccess"));
    } catch (err) {
      setProfileError(err.message || t("accountSettings.saveError"));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    if (!validatePasswordForm()) return;

    setIsSavingPassword(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    try {
      const payload = new FormData();
      payload.append("current_password", passwordForm.current_password);
      payload.append("new_password", passwordForm.new_password);
      payload.append("confirm_password", passwordForm.confirm_password);

      await userProfilesAPI.updateMySettings(payload);
      setPasswordForm(emptyPasswordForm);
      setPasswordSuccess(t("accountSettings.savePasswordSuccess"));
    } catch (err) {
      setPasswordError(err.message || t("accountSettings.saveError"));
    } finally {
      setIsSavingPassword(false);
    }
  };

  const passwordVisibilityAdornment = (show, onToggle) => (
    <InputAdornment position="end">
      <IconButton onClick={onToggle} edge="end" aria-label="toggle password" size="small">
        {show ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const passwordRules = [
    "accountSettings.passwordRule1",
    "accountSettings.passwordRule2",
    "accountSettings.passwordRule3",
    "accountSettings.passwordRule4",
    "accountSettings.passwordRule5",
    "accountSettings.passwordRule6",
  ];

  const avatarUser = picturePreview
    ? { profile_picture: picturePreview }
    : {
        profile_picture: profile?.profile_picture,
        ...profile?.user,
        first_name: profileForm.arabic_name || profile?.arabic_name,
      };

  if (isLoading) {
    return (
      <Box
        dir={isRTL ? "rtl" : "ltr"}
        sx={{
          p: { xs: 0.5, sm: 1, md: 1.5 },
          maxWidth: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 0.5, sm: 1, md: 1.5 },
        maxWidth: "100%",
        width: "100%",
      }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
          mb: 2,
          fontWeight: "bold",
        }}
      >
        {t("accountSettings.title")}
      </Typography>

      {/* Card 1: Profile data */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: 3,
          maxWidth: "100%",
          width: "100%",
        }}
      >
        {profileError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setProfileError(null)}>
            {profileError}
          </Alert>
        )}
        {profileSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setProfileSuccess(null)}>
            {profileSuccess}
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t("accountSettings.profilePicture")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            mb: 3,
            width: "100%",
          }}
        >
          <Avatar
            src={getAvatarImageSrc(avatarUser)}
            sx={{
              width: 96,
              height: 96,
              fontSize: "2rem",
              bgcolor: hasProfilePicture(avatarUser)
                ? undefined
                : getAvatarColor(profile?.user),
            }}
          >
            {hasProfilePicture(avatarUser)
              ? undefined
              : getAvatarInitials({
                  username: profile?.user?.username,
                  first_name: profileForm.arabic_name,
                })}
          </Avatar>
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              hidden
              onChange={handlePictureSelect}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={() => fileInputRef.current?.click()}
            >
              {t("accountSettings.changePhoto")}
            </Button>
            {profileErrors.profile_picture && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                {profileErrors.profile_picture}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t("accountSettings.personalInfo")}
        </Typography>
        <Box sx={{ ...formGridSx, mb: 3 }}>
          <TextField
            fullWidth
            label={t("referencePages.firstName")}
            value={profileForm.first_name}
            onChange={handleProfileChange("first_name")}
            variant="outlined"
            sx={textFieldSx}
          />
          <TextField
            fullWidth
            label={t("referencePages.lastName")}
            value={profileForm.last_name}
            onChange={handleProfileChange("last_name")}
            variant="outlined"
            sx={textFieldSx}
          />
          <TextField
            fullWidth
            required
            label={t("referencePages.arabicName")}
            value={profileForm.arabic_name}
            onChange={handleProfileChange("arabic_name")}
            error={!!profileErrors.arabic_name}
            helperText={profileErrors.arabic_name}
            variant="outlined"
            sx={textFieldSx}
          />
          <TextField
            fullWidth
            required
            label={t("referencePages.englishName")}
            value={profileForm.english_name}
            onChange={handleProfileChange("english_name")}
            error={!!profileErrors.english_name}
            helperText={profileErrors.english_name}
            variant="outlined"
            sx={textFieldSx}
          />
          <TextField
            fullWidth
            label={t("referencePages.jobTitle")}
            value={profileForm.job_title}
            variant="outlined"
            sx={textFieldSx}
            slotProps={{
              input: { readOnly: true },
            }}
          />
        </Box>

        <Box sx={actionRowSx}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSaveProfile}
            disabled={isSavingProfile}
            sx={{
              minWidth: { xs: "100%", sm: "180px" },
              height: { xs: "48px", sm: "48px" },
              fontSize: { xs: "16px", sm: "16px" },
            }}
            startIcon={
              isSavingProfile ? <CircularProgress size={20} color="inherit" /> : <Save />
            }
          >
            {t("accountSettings.saveProfile")}
          </Button>
        </Box>
      </Paper>

      {/* Card 2: Password */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: "100%",
          width: "100%",
        }}
      >
        {passwordError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setPasswordError(null)}>
            {passwordError}
          </Alert>
        )}
        {passwordSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setPasswordSuccess(null)}>
            {passwordSuccess}
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          {t("accountSettings.changePassword")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("accountSettings.passwordHint")}
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t("accountSettings.passwordRulesTitle")}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {passwordRules.map((key) => (
              <Typography component="li" variant="body2" key={key}>
                {t(key)}
              </Typography>
            ))}
          </Box>
        </Alert>

        <Box sx={{ ...formGridSx, mb: 3 }}>
          <TextField
            fullWidth
            sx={{ gridColumn: { md: "1 / -1" }, ...textFieldSx }}
            type={showCurrentPassword ? "text" : "password"}
            label={t("accountSettings.currentPassword")}
            value={passwordForm.current_password}
            onChange={handlePasswordChange("current_password")}
            error={!!passwordErrors.current_password}
            helperText={passwordErrors.current_password}
            variant="outlined"
            InputProps={{
              endAdornment: passwordVisibilityAdornment(showCurrentPassword, () =>
                setShowCurrentPassword((v) => !v)
              ),
            }}
          />
          <Box>
            <TextField
              fullWidth
              type={showNewPassword ? "text" : "password"}
              label={t("accountSettings.newPassword")}
              value={passwordForm.new_password}
              onChange={handlePasswordChange("new_password")}
              error={!!passwordErrors.new_password}
              helperText={passwordErrors.new_password}
              variant="outlined"
              sx={textFieldSx}
              InputProps={{
                endAdornment: passwordVisibilityAdornment(showNewPassword, () =>
                  setShowNewPassword((v) => !v)
                ),
              }}
            />
            <PasswordStrengthBar password={passwordForm.new_password} />
          </Box>
          <TextField
            fullWidth
            type={showConfirmPassword ? "text" : "password"}
            label={t("accountSettings.confirmPassword")}
            value={passwordForm.confirm_password}
            onChange={handlePasswordChange("confirm_password")}
            error={!!passwordErrors.confirm_password}
            helperText={passwordErrors.confirm_password}
            variant="outlined"
            sx={textFieldSx}
            InputProps={{
              endAdornment: passwordVisibilityAdornment(showConfirmPassword, () =>
                setShowConfirmPassword((v) => !v)
              ),
            }}
          />
        </Box>

        <Box sx={actionRowSx}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSavePassword}
            disabled={isSavingPassword || !passwordSaveEnabled}
            sx={{
              minWidth: { xs: "100%", sm: "220px" },
              height: { xs: "48px", sm: "48px" },
              fontSize: { xs: "16px", sm: "16px" },
            }}
            startIcon={
              isSavingPassword ? <CircularProgress size={20} color="inherit" /> : <Save />
            }
          >
            {t("accountSettings.savePassword")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SettingsPage;
